import styled, { css } from "@rentiohq/web-theme";
import { RM_COLORS } from "../../rentmatch.colors";

export const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: white;
  overflow-x: auto;
  overflow-y: visible;
`;

/* Shared grid: checkbox | candidate | rentmatch | activity | income | more-info | rating | actions */
/* The Rentmatch column needs to fit its "Rentmatch" header label plus the sort
   arrow — 80px was too narrow and let the arrow spill into the Activity column. */
const GRID_COLUMNS =
  "32px minmax(140px, 1.2fr) 110px minmax(130px, 0.9fr) 110px minmax(110px, 0.9fr) 100px 280px";

export const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding: 12px 0;
  margin: 0;
  border-bottom: 2px solid ${({ theme }) => theme.getColor("gray", 200)};
  background: white;
  min-width: fit-content;
`;

/**
 * The rows that arrived on the last load, lit for a few seconds and then
 * ordinary again (DEV-3272).
 *
 * Deliberately temporary. A marking that survives would have to remember, per
 * broker and per candidacy, what had been looked at, and it would then be
 * trusted as a work list while living in browser storage that a cleared cache
 * or a new machine wipes without a word. The list already sorts newest first,
 * so what this points at stays at the top after the light goes out.
 */
const HIGHLIGHT_FADE_MS = 1200;

export const TableRow = styled.div<{
  isDragging?: boolean;
  hasSeparator?: boolean;
  $isNew?: boolean;
}>`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding: 16px 0;
  /* Same convention as the shared ResourceList: every row gets a separator
     except the last one, so the list does not end on a dangling line. */
  ${({ theme, hasSeparator = true }) =>
    hasSeparator
      ? css`
          border-bottom: 1px solid ${theme.getColor("gray", 200)};
        `
      : ""}
  background: ${({ $isNew }) => ($isNew ? RM_COLORS.blueBg : "white")};
  transition:
    box-shadow 150ms ease,
    background ${HIGHLIGHT_FADE_MS}ms ease;
  min-width: fit-content;
  cursor: ${({ isDragging }) =>
    isDragging ? "grabbing" : "pointer"} !important;

  ${({ isDragging }) =>
    isDragging &&
    css`
      background: white;
      box-shadow: 0 5px 15px rgba(6, 44, 82, 0.15);
      border-radius: 6px;
      opacity: 0.6;
      width: 600px !important;
      overflow: hidden;
    `}
`;

export const HeaderCell = styled.div<{ sortable?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: rgb(73, 73, 73);
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  white-space: nowrap;

  ${({ sortable }) =>
    sortable &&
    css`
      cursor: pointer;
      user-select: none;

      &:hover {
        color: ${({ theme }: { theme: any }) => theme.colors.neutral100};
      }
    `}
`;

/**
 * The cell holding the select-all checkbox.
 *
 * That checkbox is a 24px button while the column labels are a 14px text line,
 * so the header grew taller the moment a second candidate appeared and the
 * checkbox showed up, and shrank again when the list dropped back to one. A
 * fixed height on this one cell keeps the header exactly as tall either way.
 * The checkbox overflows a few pixels above and below, which is not clipped and
 * stays centered, so it looks unchanged.
 */
export const HeaderCheckboxCell = styled(HeaderCell)`
  height: 18px;
`;

export const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
`;

export const CandidateNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  cursor: pointer;
`;

export const CandidateName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral100};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The language behind the main applicant's name is metadata, not part of the
   name: lighter, smaller and not semi-bold, the way the tenant profile shows
   it. Rendering it with the name's own weight and color reads as if it were
   part of the name. */
export const CandidateLanguage = styled.span`
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral70};
`;

export const CandidateMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral70};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RentMatchBadge = styled.span<{
  bgColor?: string;
  textColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ theme, bgColor }) => bgColor || theme.getColor("gray", 100)};
  color: ${({ theme, textColor }) => textColor || theme.colors.neutral70};
`;

// cspell:disable
export const IncomeText = styled.span<{ isInsufficient?: boolean }>`
  font-size: 13px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  color: ${({ isInsufficient, theme }) =>
    isInsufficient ? RM_COLORS.red : theme.colors.neutral100};
`;
// cspell:enable

/* The "More info" column holds the household counts and, once the rental CV is in, the
   document counts. They are all the same icon-plus-number shape and sit on one
   line, centred; the line wraps only when the column is too narrow to fit them
   all, which keeps a single indicator from being pushed onto a row of its own. */
export const InfoGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 12px;
  row-gap: 4px;
  min-width: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

export const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  /* The icon holds its size. It is the first thing a flex row squeezes when the
     value beside it is long, and a squeezed icon is a cropped one: which is why
     only the move-in date, whose value is a full date rather than a single
     digit, ever showed it. */
  > *:first-child {
    flex-shrink: 0;
  }

  /* And its svg gets a height, which the icon component leaves to be read off
     the viewBox. Safari on a phone does not read it there, which squashed these
     into squares on a phone while they were right on a laptop. */
  svg {
    height: 100%;
  }
`;

// The number beside an indicator icon. Red when it is what kept this candidate
// below a perfect match (DEV-3075); gray otherwise.
export const InfoValue = styled.span<{ isBelowPerfect?: boolean }>`
  font-size: 13px;
  color: ${({ theme, isBelowPerfect }) =>
    isBelowPerfect ? theme.getColor("red", 800) : theme.colors.neutral70};
`;

// The delivered half of a document fraction, red as soon as one is short. The
// total beside it keeps the color of the value it sits in.
export const DeliveredCount = styled.span<{ isIncomplete?: boolean }>`
  color: ${({ theme, isIncomplete }) =>
    isIncomplete ? theme.getColor("red", 800) : "inherit"};
`;

// The euro glyph standing in for an icon on the income document indicator.
export const IncomeDocumentGlyph = styled.span`
  font-size: 14px;
  font-weight: 100;
  line-height: 1;
`;

export const ActionsCell = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  height: 32px;
  gap: 0;
`;

export const SortIcon = styled.span<{ sorted?: boolean }>`
  display: inline-flex;
  color: ${({ theme, sorted }) =>
    sorted ? "inherit" : theme.getColor("gray", 400)};
`;

export const ActionButton = styled.button<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  /* A minimum rather than a fixed width, so a longer label in another language
     is not clipped. No label changes length when its action is switched on, so
     the row of buttons keeps its width and cannot grow over the stars. */
  min-width: 120px;
  height: 100%;
  padding: 0 12px;
  box-sizing: border-box;
  line-height: 1;
  border: 1px solid ${RM_COLORS.gray300};
  border-radius: 0;
  background: white;
  color: ${RM_COLORS.gray700};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  /* First in group: round left corners */
  &:first-child {
    border-radius: 6px 0 0 6px;
  }

  /* Collapse shared border with previous button */
  & + & {
    margin-left: -1px;
  }

  &:hover {
    background: ${RM_COLORS.gray50};
    border-color: ${RM_COLORS.gray400};
    z-index: 1;
  }

  svg {
    flex-shrink: 0;
  }

  /* Switched on, said in color instead of in a longer label. Filled red shouted
     across the row, so it is the pale red the Rentmatch score already uses for a
     low score, with the text and the thumb in the deep red that goes with it.
     The border stays the gray every action button has, so the row of buttons
     keeps one outline and only the inside of this one changes. Last in the
     block so the background also wins over the hover rule above. */
  ${({ $isActive }) =>
    $isActive
      ? `
    background: ${RM_COLORS.redBg};
    color: ${RM_COLORS.redDark};

    &:hover {
      background: ${RM_COLORS.redBg};
    }
  `
      : ""}
`;

export const ChevronButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 100%;
  box-sizing: border-box;
  line-height: 1;
  border: 1px solid ${RM_COLORS.gray300};
  border-radius: 0 6px 6px 0;
  background: white;
  color: ${RM_COLORS.gray500};
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  transition: background 120ms ease;

  &:hover {
    background: ${RM_COLORS.gray50};
    z-index: 1;
  }
`;

export const DragPreviewPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.getColor("green", 100)};
  color: ${({ theme }) => theme.getColor("green", 800)};
  border: 1px solid ${({ theme }) => theme.getColor("green", 200)};
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
`;

export const ChevronMenuWrapper = styled.div`
  display: flex;
  height: 100%;
  margin-left: -1px;
  z-index: 0;

  &:hover {
    z-index: 1;
  }
`;
